import { useMemo } from 'react';
import { Chart as ChartJS, ArcElement, Tooltip } from 'chart.js';
import { Doughnut } from 'react-chartjs-2';
import { useChartTheme } from '../../../utils/chartTheme';

import './band-donut.css';

/**
 * A count split into bands, as a doughnut.
 *
 * Deliberately a summary and nothing else: how many fall in each band,
 * and no names, no rows, no individual answers. Those live on the
 * feedback page for anyone who wants to read them.
 *
 * The hole carries the total, because the one number a reader wants
 * before any slice of it is "out of how many".
 */

ChartJS.register(ArcElement, Tooltip);

const BandDonut = ({ segments = [], centreValue, centreLabel, empty }) => {
  /* The slices keep the colours the caller hands in: they are rating
     tones (feedbackBands.js RATING_TONE), a judgement being reported,
     and never follow the palette. Only the chrome does -- the gap
     between slices and the tooltip. */
  const theme = useChartTheme();
  const shown = useMemo(() => segments.filter((s) => s.value > 0), [segments]);
  const total = useMemo(
    () => segments.reduce((n, s) => n + (Number(s.value) || 0), 0),
    [segments],
  );

  const data = useMemo(
    () => ({
      labels: shown.map((s) => s.label),
      datasets: [
        {
          data: shown.map((s) => s.value),
          backgroundColor: shown.map((s) => s.color),
          /* A 2px gap in the surface colour between slices rather than
             none: two bands of a similar weight otherwise read as one.
             The surface, not a literal white, so the gap is the card the
             ring sits on in every palette. */
          borderColor: theme.surface,
          borderWidth: 2,
          hoverOffset: 4,
        },
      ],
    }),
    [shown, theme],
  );

  const options = useMemo(
    () => ({
      responsive: true,
      maintainAspectRatio: false,
      /* Wide enough to read the total in, narrow enough that the ring
         still reads as a proportion rather than a circle of text. */
      cutout: '68%',
      animation: { duration: 260 },
      plugins: {
        /* The key is HTML beside the ring: Chart.js legends cannot
           carry a count, and a legend that only names the bands makes
           the reader hover every slice to find the numbers. */
        legend: { display: false },
        tooltip: {
          ...theme.tooltip,
          callbacks: {
            label: (ctx) => {
              const value = ctx.parsed;
              /* The API's share when the segment carries one (the
                 feedback summary's rating groups do); worked out here
                 only for callers whose segments do not come with it. */
              const given = shown[ctx.dataIndex]?.share;
              const share = typeof given === 'number'
                ? given
                : (total ? Math.round((value / total) * 100) : 0);
              return ` ${value} of ${total} (${share}%)`;
            },
          },
        },
      },
    }),
    [total, shown, theme],
  );

  /* A ring drawn from nothing is a grey circle, which reads as a chart
     that failed rather than as an honest "nothing yet". */
  if (!total) {
    return <p className="bd-empty">{empty || 'Nothing to chart yet.'}</p>;
  }

  return (
    <div className="bd">
      <div className="bd-ring">
        <Doughnut data={data} options={options} />
        <div className="bd-centre" aria-hidden="true">
          <b>{centreValue}</b>
          <span>{centreLabel}</span>
        </div>
      </div>

      {/* The same figures in text, for anyone the ring does not serve —
          and the only place the counts are readable without hovering. */}
      <ul className="bd-key">
        {segments.map((s) => (
          <li className="bd-key-row" key={s.label}>
            <span className="bd-key-dot" style={{ background: s.color }} />
            <span className="bd-key-label">{s.label}</span>
            <b className="bd-key-n">{s.value}</b>
          </li>
        ))}
      </ul>
    </div>
  );
};

export default BandDonut;
